import { useGetProviderConfig, useUpsertProviderConfig } from "@/hooks/useProviderConfig"
import { useEffect } from "react"
import { useForm } from "react-hook-form"
import { useApp } from "../../../AppProvider"
import { Spinner } from "../../common"
import { ErrorMessage } from "../../common/Feedback/ErrorMessage"

interface AntechConfigFormProps {
  orgId: string
}

interface AntechConfig {
  clinicId: string
  username: string
  password: string
}

export const AntechConfigForm = ({ orgId }: AntechConfigFormProps) => {
  const { token, addToast } = useApp()

  const { data: initialConfig, isLoading, isError, error } = useGetProviderConfig(orgId, "ANTECH", token)

  const {
    register,
    handleSubmit,
    formState: { isSubmitting, errors, isDirty },
    reset,
  } = useForm<AntechConfig>({
    defaultValues: {
      clinicId: initialConfig?.config?.clinicId || "",
      username: initialConfig?.config?.username || "",
      password: initialConfig?.config?.password || "",
    },
  })

  useEffect(() => {
    if (initialConfig?.config) {
      reset({
        clinicId: initialConfig.config.clinicId || "",
        username: initialConfig.config.username || "",
        password: initialConfig.config.password || "",
      })
    }
  }, [initialConfig, reset])

  const mutation = useUpsertProviderConfig(orgId, "ANTECH", token)

  const onSubmit = handleSubmit(async (data) => {
    const res = await mutation.mutateAsync({
      provider: "ANTECH",
      config: data,
    })
    if (res.data) {
      addToast("Success", "Antech config saved", "success")
    } else {
      addToast("Error", "Failed to save config", "error")
    }
  })

  if (isLoading) return <Spinner />

  if (isError && !error.message.includes("not found")) return <ErrorMessage message={error?.message} />

  return (
    <div>
      <h3 className="text-md font-semibold text-gray-800">Antech</h3>
      <form onSubmit={onSubmit} className="space-y-4">
        <div>
          <label className="label">Clinic ID</label>
          <input className="input" {...register("clinicId", { required: "Clinic ID is required" })} />
          {errors.clinicId && <div className="error-message">{errors.clinicId.message}</div>}
        </div>
        <div>
          <label className="label">Username</label>
          <input className="input" {...register("username", { required: "Username is required" })} />
          {errors.username && <div className="error-message">{errors.username.message}</div>}
        </div>
        <div>
          <label className="label">Password</label>
          <input
            className="input"
            type="password"
            {...register("password", { required: "Password is required" })}
          />
          {errors.password && <div className="error-message">{errors.password.message}</div>}
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            className="btn-secondary"
            disabled={!isDirty}
            onClick={() =>
              reset({
                clinicId: initialConfig?.config?.clinicId || "",
                username: initialConfig?.config?.username || "",
                password: initialConfig?.config?.password || "",
              })
            }
          >
            Cancel
          </button>

          <button className="btn" type="submit" disabled={!isDirty || isSubmitting || mutation.isPending}>
            {mutation.isPending ? "Saving..." : "Save"}
          </button>
        </div>
      </form>
    </div>
  )
}
